import { AfterViewInit, Component, ElementRef, Inject, Injector, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { runAfterHydration } from '../shared/run-after-hydration';
import { initPageScrollReveal } from '../shared/scroll-reveal.util';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModalModule],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ContactComponent implements AfterViewInit, OnDestroy {
  @ViewChild('thankYouModal') thankYouModal: any;
  @ViewChild('contactFormRef') contactFormRef?: ElementRef<HTMLFormElement>;
  @ViewChild('contactRoot') contactRoot?: ElementRef<HTMLElement>;

  nombre = '';
  email = '';
  telefono = '';
  empresa = '';
  servicio = '';
  mensaje = '';
  aceptoPrivacidad = false;
  sending = false;
  submitted = false;
  errorMessage = '';

  /** Tiempo de espera tras un envío exitoso (ms). 24 h = 86400000. Persiste al cerrar la pestaña (localStorage). */
  private readonly submitCooldownMs = 24 * 60 * 60 * 1000;
  private readonly storageKeyLastSuccess = 'grupodingo_contact_last_success';

  /** Web3Forms: envío directo del formulario */
  private readonly web3formsUrl = 'https://api.web3forms.com/submit';
  private readonly web3formsAccessKey = environment.web3formsAccessKey;

  private readonly useHCaptchaWithWeb3Forms = !!environment.web3formsEnableHCaptcha;
  private readonly web3FormsClientScriptId = 'web3forms-client-script';
  private gsapCleanups: Array<() => void> = [];

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private injector: Injector
  ) {}

  ngAfterViewInit(): void {
    void this.initHCaptcha();
    runAfterHydration(this.injector, () => void this.setupScrollRevealAnimation());
  }

  ngOnDestroy(): void {
    this.gsapCleanups.forEach((cleanup) => cleanup());
    this.gsapCleanups = [];
  }

  get shouldShowHCaptcha(): boolean {
    return this.useHCaptchaWithWeb3Forms;
  }

  private getHCaptchaToken(): string {
    const el = this.contactFormRef?.nativeElement?.querySelector(
      'textarea[name="h-captcha-response"]'
    ) as HTMLTextAreaElement | null;
    return (el?.value || '').trim();
  }

  private async initHCaptcha(): Promise<void> {
    if (typeof window === 'undefined') return;
    if (!this.useHCaptchaWithWeb3Forms) return;
    await this.loadWeb3FormsClientScript();
  }

  private loadWeb3FormsClientScript(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    const existing = document.getElementById(this.web3FormsClientScriptId) as HTMLScriptElement | null;
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = this.web3FormsClientScriptId;
      script.src = 'https://web3forms.com/client/script.js';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('No se pudo cargar el script de hCaptcha de Web3Forms.'));
      document.head.appendChild(script);
    });
  }

  /** Dispara el evento de conversión de Google Ads (lead) solo cuando el formulario se envía con éxito */
  private trackLeadConversion(): void {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-11078279371/ZT3rCNKOgf8bEMvBxKIp',
        value: 1.0,
        currency: 'MXN'
      });
    }
  }

  /** True si aún no puede enviar otro mensaje por cooldown (persiste al cerrar pestaña con localStorage) */
  isInCooldown(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const last = localStorage.getItem(this.storageKeyLastSuccess);
    if (!last) return false;
    const elapsed = Date.now() - Number(last);
    return elapsed < this.submitCooldownMs;
  }

  /** Tiempo restante de cooldown en ms (para mensajes al usuario) */
  getCooldownRemainingMs(): number {
    if (typeof window === 'undefined' || !window.localStorage) return 0;
    const last = localStorage.getItem(this.storageKeyLastSuccess);
    if (!last) return 0;
    const elapsed = Date.now() - Number(last);
    return Math.max(0, this.submitCooldownMs - elapsed);
  }

  /** Texto amigable del tiempo restante (ej. "2 horas" o "45 minutos") */
  getCooldownMessage(): string {
    const ms = this.getCooldownRemainingMs();
    if (ms <= 0) return '';
    const minutes = Math.ceil(ms / 60000);
    if (minutes >= 60) {
      const hours = Math.ceil(minutes / 60);
      return `${hours} hora${hours !== 1 ? 's' : ''}`;
    }
    return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
  }

  /** El botón Enviar solo se habilita cuando todos los campos están completos, se acepta privacidad y no hay cooldown */
  isFormValid(): boolean {
    if (this.isInCooldown()) return false;
    const hCaptchaOk = !this.useHCaptchaWithWeb3Forms || !!this.getHCaptchaToken();
    return !!(
      this.nombre?.trim() &&
      this.email?.trim() &&
      this.telefono?.trim() &&
      this.mensaje?.trim() &&
      this.aceptoPrivacidad &&
      hCaptchaOk
    );
  }

  onSubmit(): void {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.nombre?.trim() || !this.email?.trim() || !this.telefono?.trim() || !this.mensaje?.trim()) {
      this.errorMessage = 'Por favor completa todos los campos obligatorios (nombre, correo, teléfono y mensaje).';
      return;
    }
    if (!this.aceptoPrivacidad) {
      this.errorMessage = 'Debes aceptar el uso de tus datos para enviar el formulario.';
      return;
    }
    if (this.isInCooldown()) {
      this.errorMessage = `Ya enviaste un mensaje. Por favor, inténtalo de nuevo más tarde.`;
      return;
    }
    const hCaptchaToken = this.getHCaptchaToken();
    if (this.useHCaptchaWithWeb3Forms && !hCaptchaToken) {
      this.errorMessage = 'Confirma el captcha hCaptcha para poder enviar el formulario.';
      return;
    }
    this.sending = true;

    if (!this.web3formsAccessKey?.trim()) {
      this.sending = false;
      this.errorMessage = 'Falta configurar Web3Forms. Revisa `environment.ts` y `environment.prod.ts`.';
      return;
    }

    const formData = new FormData();
    formData.append('access_key', this.web3formsAccessKey);
    if (this.useHCaptchaWithWeb3Forms && hCaptchaToken) {
      formData.append('h-captcha-response', hCaptchaToken);
    }
    // Nombres estándar (lo más común en Web3Forms)
    formData.append('name', this.nombre.trim());
    formData.append('email', this.email.trim());
    formData.append('phone', this.telefono?.trim() || '');
    if (this.empresa?.trim()) formData.append('company', this.empresa.trim());
    if (this.servicio?.trim()) formData.append('service', this.servicio.trim());
    formData.append('message', this.mensaje.trim());

    // Nombres alternativos por si los tienes mapeados con los labels del frontend
    formData.append('nombre', this.nombre.trim());
    formData.append('telefono', this.telefono?.trim() || '');
    if (this.empresa?.trim()) formData.append('empresa', this.empresa.trim());
    if (this.servicio?.trim()) formData.append('servicio', this.servicio.trim());
    formData.append('mensaje', this.mensaje.trim());

    this.http.post<any>(this.web3formsUrl, formData).subscribe({
      next: (res) => {
        this.sending = false;
        if (res && (res.success === true || res.status === 200 || res.status === '200')) {
          this.submitted = false;
          this.nombre = '';
          this.email = '';
          this.telefono = '';
          this.empresa = '';
          this.servicio = '';
          this.mensaje = '';
          this.aceptoPrivacidad = false;
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(this.storageKeyLastSuccess, String(Date.now()));
          }
          this.modalService.open(this.thankYouModal, {
            centered: true,
            size: 'md',
            windowClass: 'contact-thankyou-modal'
          });
          this.trackLeadConversion();
        } else {
          this.errorMessage =
            res?.message ||
            'No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos a contacto@grupodingo.com.mx';
        }
      },
      error: (err) => {
        this.sending = false;
        const web3Message =
          err?.error?.message ||
          err?.error?.errors?.[0]?.message ||
          err?.message ||
          '';
        this.errorMessage =
          web3Message || 'No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos a contacto@grupodingo.com.mx';
      }
    });
  }

  private async setupScrollRevealAnimation(): Promise<void> {
    if (typeof window === 'undefined') return;

    const root = this.contactRoot?.nativeElement;
    if (!root) return;

    await initPageScrollReveal(root, this.gsapCleanups);
  }
}
